<script lang="ts">
  import type { FetchResult } from '@apollo/client'
  import { createEventDispatcher, getContext, onMount } from 'svelte'
  import type { ReadableQuery, MembershipsPayload, Membership, Inventory, PreviewCancelPayload } from '../../api'
  import {
    previewCancelMembership,
    cancelMembership,
    changeMembership,
    forceCancelMembership,
    getOrder,
  } from '../../api'
  import { loadingChange, loadingCancel, ordersAwaitRefetch } from '../store'
  import { profileContextKey } from '../context'
  import { Buttons } from '../../elements'

  export let inventories: Inventory[]
  export let membership: Membership
  export let activeMembershipQuery: ReadableQuery<MembershipsPayload | MembershipByEmailPayload>

  let previewCancel: FetchResult<PreviewCancelPayload>
  let error
  let isForceRefund: boolean
  let isVip: boolean

  $: isVip = membership?.inventoryId && inventories.some((i) => i.id === membership.inventoryId && i.isVip)

  const profileContext = getContext(profileContextKey)
  const userId = profileContext.getUserId()
  const { province } = profileContext.getProfile()

  const dispatch = createEventDispatcher()

  async function disableAutoRenew() {
    const input = { status: 'canceled' }
    await changeMembership(userId, input)

    await activeMembershipQuery.startPolling(200)

    dispatch('confirm')
  }

  async function cancelAndRefund() {
    try {
      loadingChange.set(true)
      await cancelMembership(membership.id, membership.orderId, membership.userId)
    } finally {
      loadingChange.set(false)
    }

    loadingCancel.set(true)
    await activeMembershipQuery.startPolling(200)

    dispatch('confirm')
    ordersAwaitRefetch.set(true)
  }

  async function cancelVip() {
    try {
      loadingChange.set(true)
      await forceCancelMembership(membership.id, membership.orderId, membership.userId)
    } finally {
      loadingChange.set(false)
    }

    loadingCancel.set(true)
    await activeMembershipQuery.startPolling(200)

    dispatch('confirm')
    ordersAwaitRefetch.set(true)
  }

  onMount(async () => {
    if (!membership.id || isVip) {
      return
    }
    try {
      previewCancel = await previewCancelMembership(membership.id, membership.orderId, membership.userId)
      if (previewCancel?.data?.previewCancelMembership.data.refundType === 'none') {
        const orderQuery = getOrder(membership.orderId)
        const orderQueryResult = await orderQuery.refetch()
        const order = orderQueryResult.data?.order.data
        isForceRefund = order?.payment?.refundType === 'forced'
      }
    } catch (e) {
      error = e
    }
  })
</script>

{#if membership?.id}
  {#if isVip}
    <h2>Cancel plan</h2>
    <div class="current membership">
      <span class="membership label">Current: </span>
      <span class="membership name" data-test="current-membership-name">1 Year VIP </span>
      <span class="membership price" data-test="current-membership-price">$0/yr</span>
    </div>
    <hr />
    <h3>Refund</h3>
    <p data-cy="previewCancel-refundDesc">No refund will be issued.</p>
    <h3 style="margin-top: 48px;">Effective</h3>
    <p data-cy="previewCancel-effectiveDesc">Account Benefits will be canceled once the membership is canceled.</p>
  {:else if previewCancel}
    <h2>Cancel plan</h2>
    <div class="current membership">
      <span class="membership label">Current: </span>
      <span data-test="current-membership-name" class="membership name">{membership.name}</span>
      <span data-test="current-membership-price" class="membership price">
        ${(membership.price / 100).toFixed(2)}/yr
      </span>
    </div>
    <hr />
    <h3>Refund</h3>
    <p data-cy="previewCancel-refundDesc">
      {#if previewCancel.data.previewCancelMembership.data.refundType === 'full'}
        Account has been open less than 60 days a full refund of ${(
          previewCancel.data.previewCancelMembership.data.amount / 100
        ).toFixed(2)}
        will be issued.
      {:else if previewCancel.data.previewCancelMembership.data.refundType === 'partial'}
        A prorated refund of ${(previewCancel.data.previewCancelMembership.data.amount / 100).toFixed(2)}
        will be issue as required by the state of
        {province}
      {:else if previewCancel.data.previewCancelMembership.data.refundType === 'none'}
        {#if isForceRefund}
          Account has been issued "force refund" before, no refund will be issued here.
        {:else}Account has been open more than 60 days, no refund will be issued.
        {/if}
      {/if}
    </p>

    <h3 style="margin-top: 48px;">Effective</h3>
    <p data-cy="previewCancel-effectiveDesc">
      {#if previewCancel.data.previewCancelMembership.data.refundType === 'full'}
        Account benefits will be cancelled immediately since the payment is being refunded. Account has been open less
        than 60 days a full refund of ${(previewCancel.data.previewCancelMembership.data.amount / 100).toFixed(2)}
        will be issued.
      {:else}
        Account Benefits will be cancelled at the end of the membership term,
        {new Date(membership.expireAt).toLocaleDateString()}.
      {/if}
    </p>
  {:else if error}{error}{:else}Loading cancellation...
  {/if}

  {#if isVip || previewCancel}
    <Buttons
      confirm={$loadingChange ? 'Canceling...' : 'Cancel Plan'}
      cancel="Back"
      confirmDisabled={$loadingChange}
      cancelDisabled={$loadingChange}
      on:confirm={isVip
        ? cancelVip
        : previewCancel.data.previewCancelMembership.data.refundType === 'none'
        ? disableAutoRenew
        : cancelAndRefund}
      on:cancel
    />
  {/if}
{/if}

<style>
  hr {
    height: 1px;
    background-color: #dee0e2;
    border: none;
    margin: 24px 0;
  }

  h2 {
    font-weight: 600;
  }

  h2,
  .membership {
    font-size: 16px;
    line-height: 22px;
  }

  .membership.label {
    color: #8e949a;
  }

  .membership.price {
    margin-left: 1em;
  }

  .membership.current {
    margin-bottom: 1em;
  }

  h3 {
    margin-top: 24px;
  }

  p {
    font-weight: 400;
    color: #484c51;
  }
</style>
